import React, { useState, useEffect } from 'react';
import { alertMonitoringApi } from '../api/adminAlert.api';
import PageHeader from '../components/PageHeader';

const AlertHistory = () => {
  const [alerts, setAlerts] = useState([]);
  const [alertTypes, setAlertTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    search: ''
  });

  useEffect(() => {
    fetchTypes();
  }, []);

  useEffect(() => {
    fetchHistory(pagination.page);
  }, [filters]); // Re-fetch on filter change

  const fetchTypes = async () => {
    try {
      const res = await alertMonitoringApi.getAppAlertTypes();
      if (res.success) {
        setAlertTypes(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch alert types for filter:', err);
    }
  };

  const fetchHistory = async (page) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pagination.limit,
        ...filters
      };
      
      const res = await alertMonitoringApi.getHistory(params);
      if (res.success) {
        setAlerts(res.data);
        setPagination(res.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch alert history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.pages) {
      fetchHistory(newPage);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      triggered: 'bg-red-100 text-red-800',
      active: 'bg-orange-100 text-orange-800',
      escalated: 'bg-purple-100 text-purple-800',
      acknowledged: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800',
      false_alarm: 'bg-yellow-100 text-yellow-800'
    };
    return `px-2 py-1 text-xs font-semibold rounded-full ${statusClasses[status] || statusClasses.cancelled}`;
  };

  return (
    <>
      <PageHeader
        title="Alert History"
        icon="fa-clock-rotate-left"
        trail={[{ label: 'Alerts' }, { label: 'History' }]}
      />

      <div className="bg-white rounded-xl shadow p-4 mb-6 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status Filter</label>
            <select 
              name="status" 
              value={filters.status} 
              onChange={handleFilterChange}
              className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Statuses</option>
              <option value="triggered">Triggered</option>
              <option value="active">Active</option>
              <option value="escalated">Escalated</option>
              <option value="resolved">Resolved</option>
              <option value="false_alarm">False Alarm</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Alert Type Filter</label>
            <select 
              name="type" 
              value={filters.type} 
              onChange={handleFilterChange}
              className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Types</option>
              {alertTypes.map(type => (
                <option key={type.id} value={type.code}>{type.name}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search User</label>
            <input 
              type="text" 
              name="search" 
              placeholder="Search by name or email..." 
              value={filters.search} 
              onChange={handleFilterChange}
              className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden border border-gray-100">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alert ID / Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Triggered At</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resolved At</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="5" className="px-6 py-10 text-center text-gray-500">Loading history...</td>
              </tr>
            ) : alerts.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-10 text-center text-gray-500">No alerts found matching your criteria.</td>
              </tr>
            ) : (
              alerts.map((alert) => (
                <tr key={alert.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{alert.type.replace('_', ' ').toUpperCase()}</div>
                    <div className="text-xs text-gray-500 font-mono mt-1">{alert.id.substring(0, 8)}...</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {alert.user ? (
                      <>
                        <div className="text-sm text-gray-900 font-medium">{alert.user.firstName} {alert.user.lastName}</div>
                        <div className="text-xs text-gray-500">{alert.user.email}</div>
                      </>
                    ) : (
                      <div className="text-sm text-gray-500 italic">Unknown</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(alert.status)}>
                      {alert.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(alert.createdAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {alert.resolvedAt ? new Date(alert.resolvedAt).toLocaleString() : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        
        {/* Pagination Controls */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> alerts
          </div>
          <div className="flex space-x-2">
            <button 
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
            >
              Previous
            </button>
            <button 
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AlertHistory;
